import { useState } from 'react';
import DashboardLayout from '@/components/dashboard/DashboardLayout';
import api from '@/lib/apiClient';
import { formatApiError } from '@/lib/utils';
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';

export default function Importacion() {
  const [archivo, setArchivo] = useState(null);
  const [subiendo, setSubiendo] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [err, setErr] = useState('');
  const [dragging, setDragging] = useState(false);

  const procesar = async (file) => {
    if (!file) return;
    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      setErr('Solo se aceptan archivos Excel (.xlsx o .xls)');
      return;
    }
    setArchivo(file); setErr(''); setResultado(null); setSubiendo(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await api.post('/api/importacion/excel', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResultado(r.data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail, 'Error al procesar el archivo'));
    } finally { setSubiendo(false); }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragging(false);
    procesar(e.dataTransfer.files[0]);
  };

  return (
    <DashboardLayout title="Importación de datos">
      <div className="max-w-2xl mx-auto">
        <p className="text-sm text-[#888] mb-6">
          Importá clientes, expedientes y honorarios desde el Excel legacy de 18 columnas.
          Los datos se crean en tu organización sin afectar a otras.
        </p>

        {/* Zona de drop */}
        <div
          className={`border-2 border-dashed rounded-2xl p-12 text-center transition-all ${
            dragging ? 'border-[#C9A44C] bg-[#C9A44C]/5' : 'border-white/10 hover:border-[#C9A44C]/40'
          }`}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}>
          <label className="cursor-pointer">
            <input type="file" accept=".xlsx,.xls" className="hidden"
              onChange={(e) => procesar(e.target.files[0])} disabled={subiendo} />
            {subiendo ? (
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="w-10 h-10 animate-spin text-[#C9A44C]" />
                <p className="text-sm text-[#C9A44C]">Procesando {archivo?.name}…</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3">
                <div className="w-16 h-16 rounded-2xl bg-[#C9A44C]/10 flex items-center justify-center">
                  <FileSpreadsheet className="w-8 h-8 text-[#C9A44C]" />
                </div>
                <div>
                  <p className="text-base font-medium mb-1">Arrastrá tu archivo Excel aquí</p>
                  <p className="text-sm text-[#888]">o hacé clic para seleccionarlo</p>
                </div>
                <p className="text-xs text-[#666]">.xlsx · .xls · Formato legacy de 18 columnas</p>
                <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm text-[#070707] font-semibold mt-2"
                  style={{ background: 'linear-gradient(135deg, #C9A44C, #E0C878)' }}>
                  <Upload className="w-4 h-4" /> Seleccionar archivo
                </div>
              </div>
            )}
          </label>
        </div>

        {/* Error */}
        {err && (
          <div className="mt-4 flex items-start gap-2 bg-red-500/10 border border-red-500/25 rounded-xl p-4 text-red-400 text-sm">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{err}</span>
          </div>
        )}

        {/* Resultado exitoso */}
        {resultado && (
          <div className="mt-4 bg-green-500/10 border border-green-500/25 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 className="w-5 h-5 text-green-400" />
              <h3 className="text-sm font-medium text-green-300">Importación completada</h3>
            </div>
            <div className="grid grid-cols-1 gap-2 text-sm">
              <div className="flex justify-between">
                <span className="text-[#888]">Registros importados</span>
                <span className="text-green-300 font-medium">{resultado.migrados}</span>
              </div>
            </div>
            <p className="text-xs text-[#888] mt-3">
              Los clientes, expedientes y honorarios ya están disponibles en el sistema.
              Podés verificarlos en las secciones correspondientes.
            </p>
          </div>
        )}

        {/* Instrucciones del formato */}
        <div className="mt-6 bg-[#0E0E0E] border border-[rgba(201,164,76,0.12)] rounded-xl p-5">
          <h3 className="text-xs uppercase tracking-[0.2em] text-[#C9A44C] mb-3">Formato esperado del Excel</h3>
          <p className="text-xs text-[#888] mb-3">El archivo debe tener las primeras 2 filas como encabezados y datos desde la fila 3:</p>
          <div className="overflow-x-auto">
            <table className="text-[10px] text-[#888] w-full">
              <thead>
                <tr className="border-b border-white/5">
                  {['Col A', 'Col B', 'Col C', 'Col D', 'Col E', 'Col F', 'Col G', 'Col H', 'Col I-R'].map((h) => (
                    <th key={h} className="text-left py-1 pr-3 text-[#666]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  {['N° Cliente', 'Apellido', 'Nombre', '—', 'Tipo proceso', 'Juzgado', 'Estado causa', 'Fecha inicio', 'Datos financieros'].map((c) => (
                    <td key={c} className="py-1 pr-3">{c}</td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
